import React, { useState, useEffect } from 'react';
import { Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { getSeoSettings, updateSeoSettings } from '@/services/settingsApi';

export const SEOSettingsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [googleAnalyticsId, setGoogleAnalyticsId] = useState('');
  const [headerScript, setHeaderScript] = useState('');
  const [bodyScript, setBodyScript] = useState('');
  const [footerScript, setFooterScript] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await getSeoSettings();
        const data = res?.data || res || {};
        setMetaTitle(data.meta_title || '');
        setMetaDescription(data.meta_description || '');
        setGoogleAnalyticsId(data.google_analytics_id || '');
        setHeaderScript(data.header_script || '');
        setBodyScript(data.body_script || '');
        setFooterScript(data.footer_script || '');
      } catch (error) {
        console.error(error);
        toast.error('Failed to load SEO settings');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSeoSettings({
        meta_title: metaTitle,
        meta_description: metaDescription,
        google_analytics_id: googleAnalyticsId,
        header_script: headerScript,
        body_script: bodyScript,
        footer_script: footerScript,
      });
      toast.success('SEO settings saved');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-xl border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg">SEO Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Meta Title</Label>
          <Input
            value={metaTitle}
            onChange={(e) => setMetaTitle(e.target.value)}
            className="mt-2"
            maxLength={60}
          />
          <p className="text-xs text-slate-500 mt-1">60 characters max</p>
        </div>
        <div>
          <Label>Meta Description</Label>
          <Textarea
            value={metaDescription}
            onChange={(e) => setMetaDescription(e.target.value)}
            className="mt-2"
            rows={3}
            maxLength={160}
          />
          <p className="text-xs text-slate-500 mt-1">160 characters max</p>
        </div>
        <div>
          <Label>Google Analytics ID</Label>
          <Input
            value={googleAnalyticsId}
            onChange={(e) => setGoogleAnalyticsId(e.target.value)}
            placeholder="G-XXXXXXXXXX"
            className="mt-2"
          />
        </div>

        <div className="pt-4 space-y-4 border-t">
          <h3 className="font-semibold text-slate-900">Custom Scripts</h3>
          <p className="text-sm text-slate-600">
            Add custom scripts for Google Tag Manager, Facebook Pixel, etc.
          </p>

          <div>
            <Label>Header Scripts</Label>
            <p className="text-xs text-slate-500 mb-2">
              Scripts added here will be inserted in the &lt;head&gt; section
            </p>
            <Textarea
              value={headerScript}
              onChange={(e) => setHeaderScript(e.target.value)}
              rows={5}
              className="mt-2 font-mono text-xs"
              placeholder="<script>...</script>"
            />
          </div>

          <div>
            <Label>Body Scripts (Top)</Label>
            <p className="text-xs text-slate-500 mb-2">
              Scripts added here will be inserted at the top of &lt;body&gt;
            </p>
            <Textarea
              value={bodyScript}
              onChange={(e) => setBodyScript(e.target.value)}
              rows={5}
              className="mt-2 font-mono text-xs"
              placeholder="<script>...</script>"
            />
          </div>

          <div>
            <Label>Footer Scripts</Label>
            <p className="text-xs text-slate-500 mb-2">
              Scripts added here will be inserted before closing &lt;/body&gt; tag
            </p>
            <Textarea
              value={footerScript}
              onChange={(e) => setFooterScript(e.target.value)}
              rows={5}
              className="mt-2 font-mono text-xs"
              placeholder="<script>...</script>"
            />
          </div>
        </div>

        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Changes
        </Button>
      </CardContent>
    </Card>
  );
};